import { useEffect, useRef, useState } from 'react';
import { ServiceSelection } from './ServiceSelection';
import { SlotSelection } from './SlotSelection';
import type { UIIntent } from '../../services/bookingService';
import { formatBookingDateShort, localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { stripEmojiUnicode } from '../../utils/stripEmoji';

function getShiftLabel(
  timeString: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const hour = parseInt(timeString.split(':')[0] ?? '12', 10);
  if (hour >= 6 && hour < 12) {
    return t('booking.shift_breakfast', { defaultValue: 'Desayuno' });
  }
  if (hour >= 12 && hour < 17) {
    return t('booking.shift_lunch', { defaultValue: 'Almuerzo' });
  }
  if (hour >= 17 && hour < 21) {
    return t('booking.shift_afternoon', { defaultValue: 'Merienda/Tarde' });
  }
  return t('booking.shift_dinner', { defaultValue: 'Cena' });
}

type ZoneOption = { value: string; label: string; capacity?: number };
type DateOption = { date: string; formatted?: string; available?: boolean };
type TimeSlot = {
  timeString: string;
  slots: Array<{ id: string; startTime: string; available: boolean; available_capacity?: number }>;
  totalCapacity: number;
  representativeSlotId: string;
};

interface RestaurantLiveBookingProps {
  uiIntent: UIIntent;
  loading: boolean;
  language: string;
  onAction: (action: string, payload: Record<string, unknown>) => Promise<void> | void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

export function RestaurantLiveBooking({
  uiIntent,
  loading,
  language,
  onAction,
  t,
}: RestaurantLiveBookingProps) {
  const bookingDateLocale = localeTagFromLang(language);
  const [services, setServices] = useState<any[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [selectedServiceName, setSelectedServiceName] = useState<string>('');
  const [serviceChosen, setServiceChosen] = useState(false);
  const [minPartySize, setMinPartySize] = useState<number>(1);
  const [maxPartySize, setMaxPartySize] = useState<number>(20);
  const [partySize, setPartySize] = useState<number>(1);
  const [zoneOptions, setZoneOptions] = useState<ZoneOption[]>([]);
  const [preferredZone, setPreferredZone] = useState<string>('');
  const [dates, setDates] = useState<DateOption[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<{ id: string; timeString?: string } | null>(null);
  const [confirmBooking, setConfirmBooking] = useState<any | null>(null);
  const [busyAction, setBusyAction] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [reservationMode, setReservationMode] = useState<'SLOT' | 'SHIFT'>('SLOT');
  const [servicePrice, setServicePrice] = useState<number | undefined>(undefined);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Tracks whether we've already done the initial availability load for this service.
  // Prevents the auto-bootstrap from running more than once per service selection.
  const bootstrappedServiceRef = useRef<string | null>(null);

  // Dedup keys for each request type
  const lastDetailsKeyRef = useRef<string>('');
  const lastDateKeyRef = useRef<string>('');

  // Ref to know if an auto-select of a single service has been done
  const autoSelectedServiceRef = useRef<string | null>(null);

  // Request versioning: seq increments with every executeAction call.
  // latestCompletedSeqRef tracks the seq of the last completed request.
  // In useEffect we only apply SelectDate/SelectSlot updates when no newer request
  // has already completed (i.e. the one that triggered THIS uiIntent is stale).
  const reqSeqRef = useRef(0);
  const latestCompletedSeqRef = useRef(0);

  const isBusy = loading || busyAction;
  const hasServiceChoices = services.length > 1;
  const showZoneControl = zoneOptions.length > 0;

  const executeAction = async (action: string, payload: Record<string, unknown>) => {
    const seq = ++reqSeqRef.current;
    setBusyAction(true);
    try {
      await onAction(action, payload);
      // Only advance the completed pointer forward — never backwards.
      // If a newer request already completed, its seq would be higher and we must not
      // overwrite latestCompletedSeqRef with an older seq.
      if (seq > latestCompletedSeqRef.current) {
        latestCompletedSeqRef.current = seq;
      }
    } finally {
      setBusyAction(false);
    }
  };

  // Sends select_restaurant_details. Deduplicates by key.
  const requestRestaurantDetails = async (
    serviceId: string | null,
    nextPartySize: number,
    nextZone: string,
  ) => {
    const key = `${serviceId}|${nextPartySize}|${nextZone}`;
    if (lastDetailsKeyRef.current === key) return;
    lastDetailsKeyRef.current = key;
    await executeAction('select_restaurant_details', {
      ...(serviceId ? { serviceId } : {}),
      party_size: nextPartySize,
      ...(nextZone ? { preferred_zone: nextZone } : {}),
    });
  };

  // Sends select_date. Deduplicates by key.
  const requestDateSlots = async (serviceId: string | null, nextDate: string) => {
    if (!nextDate) return;
    const key = `${serviceId}|${nextDate}`;
    if (lastDateKeyRef.current === key) return;
    lastDateKeyRef.current = key;
    setSlotsLoading(true);
    try {
      await executeAction('select_date', { date: nextDate });
    } finally {
      setSlotsLoading(false);
    }
  };

  // Extract zone options from intent metadata
  const extractZoneOptionsFromMeta = (meta: any): ZoneOption[] => {
    const raw = Array.isArray(meta?.zoneOptions) ? meta.zoneOptions : [];
    return raw
      .map((opt: any) => ({
        value: String(opt?.value || opt?.name || '').trim(),
        label: String(opt?.label || opt?.value || opt?.name || '').trim(),
        capacity: typeof opt?.capacity === 'number' ? opt.capacity : undefined,
      }))
      .filter((opt: ZoneOption) => opt.value.length > 0);
  };

  // Apply restaurant metadata (party size limits, zones) from any intent that carries it
  const applyRestaurantMeta = (meta: any) => {
    if (!meta) return;
    const nextMin =
      typeof meta.minPartySize === 'number' && Number.isFinite(meta.minPartySize)
        ? Number(meta.minPartySize)
        : 1;
    const nextMax =
      typeof meta.maxPartySize === 'number' && Number.isFinite(meta.maxPartySize)
        ? Number(meta.maxPartySize)
        : 20;
    const requestedPartySizeRaw =
      typeof meta.partySize === 'number'
        ? meta.partySize
        : typeof meta.party_size === 'number'
          ? meta.party_size
          : undefined;
    const normalizedRequestedPartySize =
      Number.isFinite(requestedPartySizeRaw) && (requestedPartySizeRaw as number) > 0
        ? Math.floor(requestedPartySizeRaw as number)
        : undefined;

    setMinPartySize(nextMin);
    setMaxPartySize(nextMax);
    setPartySize((prev) => {
      const base =
        typeof normalizedRequestedPartySize === 'number' ? normalizedRequestedPartySize : prev || nextMin;
      return Math.max(nextMin, Math.min(nextMax, base));
    });
    const nextZones = extractZoneOptionsFromMeta(meta);
    if (nextZones.length > 0) {
      setZoneOptions(nextZones);
    }
    // Sync preferred_zone from backend canonical value so the select always matches its options.
    // The backend always echoes it ('' means "Sin preferencia"), so we can trust it here.
    if ('preferred_zone' in meta) {
      setPreferredZone(typeof meta.preferred_zone === 'string' ? meta.preferred_zone : '');
    }
    if (meta?.reservation_mode === 'SHIFT' || meta?.reservation_mode === 'SLOT') {
      setReservationMode(meta.reservation_mode);
    }
    if (typeof meta?.price === 'number') setServicePrice(meta.price);
    if (typeof meta?.requires_payment === 'boolean') setRequiresPayment(meta.requires_payment);
  };

  // Main intent handler — called once whenever uiIntent changes
  useEffect(() => {
    const meta = (uiIntent as any)?.metadata || {};

    if (uiIntent.type === 'ShowError') {
      const msg =
        (uiIntent as any).data?.message ||
        'No hay horarios disponibles. Selecciona otra fecha.';
      setInlineError(String(msg));
      return;
    }

    // For data-bearing intents (SelectDate / SelectSlot) guard against stale responses:
    // if a newer request has already completed its round-trip, this uiIntent came from
    // an older request and applying it would overwrite fresher state.
    const isStaleDataIntent =
      (uiIntent.type === 'SelectDate' || uiIntent.type === 'SelectSlot') &&
      latestCompletedSeqRef.current > 0 &&
      reqSeqRef.current > latestCompletedSeqRef.current;
    if (isStaleDataIntent) return;

    if (uiIntent.type === 'SelectService') {
      const incoming = Array.isArray(uiIntent.data?.services) ? uiIntent.data.services : [];
      setServices(incoming);
      setConfirmBooking(null);
      setSelectedSlot(null);
      setTimeSlots([]);
      setDates([]);
      setReservationMode('SLOT');
      setServicePrice(undefined);
      setRequiresPayment(false);
      setSlotsLoading(false);
      setServiceChosen(incoming.length === 1);
      // Auto-select single service once
      if (incoming.length === 1) {
        const only = incoming[0];
        const onlyId = only?.id ? String(only.id) : '';
        if (onlyId && autoSelectedServiceRef.current !== onlyId) {
          autoSelectedServiceRef.current = onlyId;
          void (async () => {
            setSelectedServiceId(onlyId);
            setSelectedServiceName(String(only?.name || ''));
            lastDetailsKeyRef.current = '';
            lastDateKeyRef.current = '';
            await executeAction('select_service', { serviceId: onlyId });
          })();
        }
      }
      return;
    }

    if (uiIntent.type === 'ShowForm') {
      applyRestaurantMeta(meta);
      const fields = Array.isArray(uiIntent.data?.form?.fields) ? uiIntent.data.form.fields : [];
      const zoneField = fields.find((f: any) => f?.id === 'preferred_zone');
      const nextZones: ZoneOption[] = Array.isArray(zoneField?.options)
        ? zoneField.options
            .map((opt: any) => ({
              value: String(opt?.value || ''),
              label: String(opt?.label || opt?.value || ''),
              capacity: typeof opt?.capacity === 'number' ? opt.capacity : undefined,
            }))
            .filter((opt: ZoneOption) => opt.value.length > 0)
        : [];
      const nextMin = typeof meta?.minPartySize === 'number' ? Number(meta.minPartySize) : 1;
      const nextMax = typeof meta?.maxPartySize === 'number' ? Number(meta.maxPartySize) : 20;
      setZoneOptions(nextZones);
      setMinPartySize(nextMin);
      setMaxPartySize(nextMax);
      const requestedPartySizeRaw =
        typeof meta?.partySize === 'number'
          ? Number(meta.partySize)
          : typeof meta?.party_size === 'number'
            ? Number(meta.party_size)
            : undefined;
      const normalizedRequestedPartySize =
        Number.isFinite(requestedPartySizeRaw) && (requestedPartySizeRaw as number) > 0
          ? Math.floor(requestedPartySizeRaw as number)
          : undefined;
      setPartySize((prev) => {
        const base =
          typeof normalizedRequestedPartySize === 'number' ? normalizedRequestedPartySize : prev || nextMin;
        return Math.max(nextMin, Math.min(nextMax, base));
      });
      setServiceChosen(true);
      setConfirmBooking(null);

      // Bootstrap: fire select_restaurant_details once for this service
      const svcId =
        selectedServiceId ||
        String((uiIntent.data as any)?.serviceId || (uiIntent.data?.form as any)?.serviceId || '');
      if (bootstrappedServiceRef.current !== svcId) {
        bootstrappedServiceRef.current = svcId;
        // Capture current partySize and preferredZone from state
        const currentPartySize = partySize || nextMin;
        const currentZone = preferredZone;
        lastDetailsKeyRef.current = '';
        void requestRestaurantDetails(svcId || null, currentPartySize, currentZone);
      }
      return;
    }

    if (uiIntent.type === 'SelectDate') {
      applyRestaurantMeta(meta);
      const incomingDates = Array.isArray(uiIntent.data?.dates) ? uiIntent.data.dates : [];
      setDates(incomingDates);
      setServiceChosen(true);
      setConfirmBooking(null);
      setSelectedSlot(null);
      // Auto-select first available date and load slots.
      // Compute dateToLoad before the setState call to avoid the stale-closure trap
      // (selectedDate hasn't updated yet when this synchronous block runs).
      if (incomingDates.length > 0) {
        const firstDate = incomingDates.find((d: DateOption) => d.available !== false)?.date;
        if (firstDate) {
          const dateToLoad = selectedDate || firstDate;
          setSelectedDate((prev) => prev || firstDate);
          void requestDateSlots(selectedServiceId, dateToLoad);
        }
      }
      return;
    }

    if (uiIntent.type === 'SelectSlot') {
      applyRestaurantMeta(meta);
      const incomingTimeSlots = Array.isArray(uiIntent.data?.timeSlots) ? uiIntent.data.timeSlots : [];
      setTimeSlots(incomingTimeSlots);
      setServiceChosen(true);
      setConfirmBooking(null);
      const slotDate =
        typeof (uiIntent.data as any)?.slotDate === 'string' &&
        (uiIntent.data as any).slotDate.length > 0
          ? String((uiIntent.data as any).slotDate)
          : '';
      const slotFormattedDate =
        typeof (uiIntent.data as any)?.slotFormattedDate === 'string' &&
        (uiIntent.data as any).slotFormattedDate.length > 0
          ? String((uiIntent.data as any).slotFormattedDate)
          : undefined;
      if (slotDate) {
        // Preserve the full date list loaded by SelectDate.
        // Only fall back to a single-entry list when no dates have been loaded yet
        // (e.g. landing directly on a restored SelectSlot session).
        setDates((prev) =>
          prev.length > 0 ? prev : [{ date: slotDate, formatted: slotFormattedDate, available: true }],
        );
        setSelectedDate(slotDate);
      }
      setSelectedSlot((prev) => {
        if (!prev) return prev;
        const stillValid = incomingTimeSlots.some(
          (s: TimeSlot) => s.representativeSlotId === prev.id,
        );
        return stillValid ? prev : null;
      });
      return;
    }

    if (uiIntent.type === 'ConfirmBooking') {
      setServiceChosen(true);
      const booking = uiIntent.data?.booking || null;
      const mergedConfirmBooking = {
        ...booking,
        ...(typeof meta?.party_size === 'number' ? { party_size: Number(meta.party_size) } : {}),
        ...(typeof meta?.preferred_zone === 'string'
          ? { preferred_zone: String(meta.preferred_zone) }
          : {}),
        ...(typeof meta?.remaining_capacity_estimate === 'number'
          ? { remaining_capacity_estimate: Number(meta.remaining_capacity_estimate) }
          : {}),
      };
      setConfirmBooking(mergedConfirmBooking);
      // Sincronizar contadores/resumen para evitar mostrar "1 persona" al entrar en confirmación.
      const confirmedPartySizeRaw =
        typeof mergedConfirmBooking?.party_size === 'number'
          ? mergedConfirmBooking.party_size
          : typeof meta?.party_size === 'number'
            ? meta.party_size
            : undefined;
      if (typeof confirmedPartySizeRaw === 'number' && Number.isFinite(confirmedPartySizeRaw)) {
        const normalized = Math.max(1, Math.floor(confirmedPartySizeRaw));
        setPartySize((prev) => {
          const nextMin = minPartySize || 1;
          const nextMax = maxPartySize || Math.max(nextMin, normalized);
          return Math.max(nextMin, Math.min(nextMax, normalized || prev || nextMin));
        });
      }
      if (meta?.reservation_mode === 'SHIFT' || meta?.reservation_mode === 'SLOT') {
        setReservationMode(meta.reservation_mode);
      }
      if (typeof meta?.price === 'number') setServicePrice(meta.price);
      if (typeof meta?.requires_payment === 'boolean') setRequiresPayment(meta.requires_payment);
    }
  }, [uiIntent]); // Only re-run when uiIntent changes — intentional

  const handleSelectService = async (serviceId: string) => {
    const service = services.find((s) => s.id === serviceId);
    setSelectedServiceId(serviceId);
    setSelectedServiceName(service?.name || '');
    setSelectedDate('');
    setDates([]);
    setTimeSlots([]);
    setSelectedSlot(null);
    setConfirmBooking(null);
    setZoneOptions([]);
    setPreferredZone('');
    setReservationMode('SLOT');
    setServicePrice(undefined);
    setRequiresPayment(false);
    setSlotsLoading(false);
    setServiceChosen(true);
    bootstrappedServiceRef.current = null; // allow re-bootstrap after service change
    lastDetailsKeyRef.current = '';
    lastDateKeyRef.current = '';
    await executeAction('select_service', { serviceId });
  };

  const handlePartySizeChange = (nextSize: number) => {
    setPartySize(nextSize);
    setSelectedDate('');
    setDates([]);
    setTimeSlots([]);
    setSelectedSlot(null);
    setConfirmBooking(null);
    lastDetailsKeyRef.current = '';
    lastDateKeyRef.current = '';
    void requestRestaurantDetails(selectedServiceId, nextSize, preferredZone);
  };

  const handleZoneChange = (nextZone: string) => {
    setPreferredZone(nextZone);
    setSelectedDate('');
    setDates([]);
    setTimeSlots([]);
    setSelectedSlot(null);
    setConfirmBooking(null);
    lastDetailsKeyRef.current = '';
    lastDateKeyRef.current = '';
    void requestRestaurantDetails(selectedServiceId, partySize, nextZone);
  };

  const handleDateChange = (nextDate: string) => {
    setInlineError(null);
    setSelectedDate(nextDate);
    setSelectedSlot(null);
    setConfirmBooking(null);
    lastDateKeyRef.current = '';
    void requestDateSlots(selectedServiceId, nextDate);
  };

  const handleSelectSlot = (slotId: string, timeString?: string) => {
    setSelectedSlot({ id: slotId, timeString });
    setConfirmBooking(null);
    void executeAction('select_slot', { slotId, ...(timeString ? { timeString } : {}) });
  };

  const partyLabel = `${partySize} ${
    partySize === 1
      ? t('booking.person', { defaultValue: 'persona' })
      : t('booking.people', { defaultValue: 'personas' })
  }`;

  return (
    <div className="restaurant-live">
      <h2 className="restaurant-live-title">
        {t('booking.restaurant_live_title', { defaultValue: 'Reserva al vuelo' })}
      </h2>

      {uiIntent.type === 'SelectService' && hasServiceChoices ? (
        <div className="restaurant-live-block">
          <ServiceSelection
            services={services}
            onSelect={(serviceId) => void handleSelectService(serviceId)}
            language={language}
          />
        </div>
      ) : hasServiceChoices ? (
        <div className="restaurant-live-selected-service">
          <span className="restaurant-live-selected-service-label">
            {t('booking.selected_service', { defaultValue: 'Servicio seleccionado' })}
          </span>
          <strong className="restaurant-live-selected-service-name">
            {stripEmojiUnicode(
              selectedServiceName ||
                (services.length === 1 ? String(services[0]?.name || '') : '') ||
                t('booking.service_selected', { defaultValue: 'Servicio elegido' }),
            )}
          </strong>
        </div>
      ) : null}

      {serviceChosen && !confirmBooking ? (
        <>
          <div
            className="restaurant-live-controls"
            style={{ gridTemplateColumns: `repeat(${showZoneControl ? 2 : 1}, minmax(0, 1fr))` }}
          >
            <div className="restaurant-live-control">
              <label>{t('booking.party_size_label', { defaultValue: 'Personas' })}</label>
              <div className="restaurant-live-stepper">
                <button
                  type="button"
                  onClick={() => handlePartySizeChange(Math.max(minPartySize, partySize - 1))}
                  disabled={isBusy || partySize <= minPartySize}
                >
                  -
                </button>
                <span>{partyLabel}</span>
                <button
                  type="button"
                  onClick={() => handlePartySizeChange(Math.min(maxPartySize, partySize + 1))}
                  disabled={isBusy || partySize >= maxPartySize}
                >
                  +
                </button>
              </div>
            </div>

            {showZoneControl ? (
              <div className="restaurant-live-control">
                <label>{t('booking.zone_label', { defaultValue: 'Zona' })}</label>
                <select
                  value={preferredZone}
                  onChange={(e) => handleZoneChange(e.target.value)}
                  disabled={isBusy}
                >
                  <option value="">
                    {t('booking.zone_no_preference', { defaultValue: 'Sin preferencia' })}
                  </option>
                  {zoneOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {stripEmojiUnicode(opt.label)}
                      {opt.capacity != null
                        ? ` ${t('booking.zone_option_capacity', { count: opt.capacity })}`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

          </div>

          {/* Tira horizontal de fechas — muestra TODOS los días, cierra los no disponibles */}
          {dates.length > 0 ? (
            <div className="restaurant-live-date-strip-wrap">
              <p className="restaurant-live-date-label">
                {t('booking.date', { defaultValue: 'Fecha' })}
              </p>
              <div className="restaurant-live-date-strip">
                {dates.map((d) => {
                  const isAvailable = d.available !== false;
                  const isSelected = selectedDate === d.date;
                  return (
                    <button
                      key={d.date}
                      type="button"
                      disabled={!isAvailable || isBusy}
                      onClick={() => {
                        if (isAvailable && !isBusy) handleDateChange(d.date);
                      }}
                      className={[
                        'restaurant-live-date-btn',
                        isSelected ? 'restaurant-live-date-btn--active' : '',
                        !isAvailable ? 'restaurant-live-date-btn--closed' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {formatBookingDateShort(d.date, bookingDateLocale)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : !isBusy ? (
            <p className="restaurant-live-message">
              {t('booking.no_dates_available', { defaultValue: 'Sin fechas disponibles' })}
            </p>
          ) : null}

          {inlineError && !isBusy ? (
            <div className="restaurant-live-inline-error">
              {stripEmojiUnicode(inlineError)}
            </div>
          ) : null}

          {isBusy ? (
            <p className="restaurant-live-message">
              {t('booking.loading', { defaultValue: 'Cargando...' })}
            </p>
          ) : null}

          {selectedDate && slotsLoading ? (
            <div className="slots-loading">
              <span className="slots-loading-spinner" />
              <span>
                {t('booking.loading_slots', { defaultValue: 'Buscando disponibilidad...' })}
              </span>
            </div>
          ) : selectedDate && !slotsLoading && timeSlots.length > 0 ? (
            <div className="restaurant-live-block">
              {reservationMode === 'SHIFT' ? (
                <div className="shift-selection">
                  <h3 className="booking-section-title">
                    {t('booking.select_shift', { defaultValue: 'Selecciona turno' })}
                  </h3>
                  <div className="shift-grid">
                    {timeSlots.map((slot) => {
                      const label = getShiftLabel(slot.timeString, t);
                      return (
                        <button
                          key={slot.representativeSlotId}
                          type="button"
                          className={`shift-card ${selectedSlot?.id === slot.representativeSlotId ? 'shift-card--selected' : ''}`}
                          onClick={() =>
                            handleSelectSlot(slot.representativeSlotId, slot.timeString)
                          }
                          disabled={isBusy}
                        >
                          <span className="shift-card-label">{label}</span>
                          <span className="shift-card-time">{slot.timeString}</span>
                          {slot.totalCapacity < partySize * 3 && slot.totalCapacity > 0 ? (
                            <span className="shift-card-capacity">
                              {t('booking.slots_few_left', { count: slot.totalCapacity })}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <SlotSelection
                  date={selectedDate}
                  formattedDate={dates.find((d) => d.date === selectedDate)?.formatted}
                  timeSlots={timeSlots}
                  requestedPartySize={partySize}
                  onSelect={(slotId, timeString) => handleSelectSlot(slotId, timeString)}
                  language={language}
                />
              )}
            </div>
          ) : selectedDate && !slotsLoading && !isBusy && timeSlots.length === 0 ? (
            <p className="booking-no-slots">
              {t('booking.no_slots_for_group', {
                defaultValue: `No hay disponibilidad para {{count}} personas en esta fecha`,
                count: partySize,
              })}
            </p>
          ) : null}

        </>
      ) : null}

      {confirmBooking ? (
        <div className="restaurant-live-confirm">
          <h3 className="confirm-title">
            {t('booking.review_title', { defaultValue: 'Resumen de tu reserva' })}
          </h3>
          <div className="confirm-detail-card">
            <div className="confirm-detail-row">
              <span className="confirm-detail-label">
                {stripEmojiUnicode(String(selectedServiceName || confirmBooking.serviceName || ''))}
              </span>
            </div>
            <div className="confirm-detail-row">
              <span>📅</span>
              <span>
                {confirmBooking.date || selectedDate}
                {selectedSlot?.timeString ? ` · ${selectedSlot.timeString}` : ''}
              </span>
            </div>
            <div className="confirm-detail-row">
              <span>👥</span>
              <span>
                {confirmBooking.party_size ?? partySize}{' '}
                {t('booking.people', { defaultValue: 'personas' })}
              </span>
            </div>
            {confirmBooking.preferred_zone ? (
              <div className="confirm-detail-row">
                <span>📍</span>
                <span>{stripEmojiUnicode(String(confirmBooking.preferred_zone))}</span>
              </div>
            ) : null}
            {requiresPayment && servicePrice != null && servicePrice > 0 ? (
              <div className="confirm-detail-row confirm-detail-row--price">
                <span>💳</span>
                <span>
                  {t('booking.price_deposit', { defaultValue: 'Depósito' })}: {servicePrice}
                </span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="btn-primary btn-confirm"
            disabled={isBusy}
            onClick={() => void executeAction('confirm', {})}
          >
            {requiresPayment
              ? t('booking.confirm_and_pay', { defaultValue: 'Confirmar y pagar' })
              : t('booking.confirm', { defaultValue: 'Confirmar reserva' })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
